// ============================================================================
// Conditions et signes (lot 2 de la refonte, docs/moteur/REFONTE_MOTEUR.md).
//
// Décide, quand c'est possible SANS DOUTE, le signe d'un polynôme sous des
// hypothèses (« a > 0 », « x − 1 ≠ 0 ») : x² + 1 > 0, 2a > 0 si a > 0, a² ≥ 0.
// Un signe est un ENSEMBLE de possibilités {positif, nul, négatif} ; on ne
// conclut que si l'ensemble l'impose. Sinon : inconnu, jamais deviné.
// ============================================================================

import * as Q from './exact';
import {
  normalize,
  polyEqual,
  polyFloat,
  polyIsConstant,
  polyIsZero,
  polyRat,
  polyScale,
  polyTerms,
  rfEqual,
  type Atom,
  type Poly,
  type RatFn,
} from './normal';
import { parseStatement, termText, type Sorts, type Term } from './term';
import type { Condition, ConditionKind } from './types';

export interface SignSet {
  pos: boolean;
  zero: boolean;
  neg: boolean;
}
export const SIGN = {
  pos: { pos: true, zero: false, neg: false },
  neg: { pos: false, zero: false, neg: true },
  zero: { pos: false, zero: true, neg: false },
  nonzero: { pos: true, zero: false, neg: true },
  nonneg: { pos: true, zero: true, neg: false },
  nonpos: { pos: false, zero: true, neg: true },
  any: { pos: true, zero: true, neg: true },
} as const satisfies Record<string, SignSet>;

const meet = (a: SignSet, b: SignSet): SignSet => ({
  pos: a.pos && b.pos,
  zero: a.zero && b.zero,
  neg: a.neg && b.neg,
});
const mulSet = (a: SignSet, b: SignSet): SignSet => ({
  pos: (a.pos && b.pos) || (a.neg && b.neg),
  neg: (a.pos && b.neg) || (a.neg && b.pos),
  zero: a.zero || b.zero,
});
const powSet = (a: SignSet, e: number): SignSet =>
  e % 2 === 0 ? { pos: a.pos || a.neg, zero: a.zero, neg: false } : a;
const negSet = (a: SignSet): SignSet => ({ pos: a.neg, zero: a.zero, neg: a.pos });

/* ------------------------------ faits ------------------------------ */

export interface Facts {
  /** signe connu de chaque inconnue */
  sign: Map<string, SignSet>;
  /** inconnues entières */
  integer: Set<string>;
  /** hypothèses portant sur une expression : x − 1 ≠ 0 */
  polys: { p: Poly; s: SignSet }[];
  sorts: Sorts;
}

const KIND_SIGN: Partial<Record<ConditionKind, SignSet>> = {
  positive: SIGN.pos,
  negative: SIGN.neg,
  nonzero: SIGN.nonzero,
  nonnegative: SIGN.nonneg,
  zero: SIGN.zero,
  natural: SIGN.nonneg,
};

function exprOf(text: string): Term | null {
  const r = parseStatement(text);
  return r.ok && r.s.k === 'expr' ? r.s.e : null;
}

/** Hypothèses → faits. Une hypothèse illisible est ignorée (elle n'aide pas, elle ne trompe pas). */
export function factsFrom(hyps: readonly Condition[] = [], sorts: Sorts = {}): Facts {
  const facts: Facts = { sign: new Map(), integer: new Set(), polys: [], sorts };
  for (const h of hyps) addFact(facts, h);
  return facts;
}
export function addFact(facts: Facts, h: Condition): void {
  const t = h.of.length === 1 ? exprOf(h.of[0]!) : null;
  if (!t) return;
  if ((h.kind === 'integer' || h.kind === 'natural') && t.k === 'sym') facts.integer.add(t.n);
  const s = KIND_SIGN[h.kind];
  if (!s) return;
  if (t.k === 'sym') {
    facts.sign.set(t.n, meet(facts.sign.get(t.n) ?? SIGN.any, s));
    return;
  }
  const n = normalize(t, { sorts: facts.sorts });
  if (n.ok && polyRat(n.rf.d) !== null) facts.polys.push({ p: n.rf.n, s });
}

/* ------------------------------ signes ------------------------------ */

function atomSign(a: Atom, facts: Facts): SignSet {
  if (a.kind === 'sym') return facts.sign.get(a.symbol!) ?? SIGN.any;
  if (a.kind === 'root') return SIGN.pos;
  if (a.constant) {
    const v = a.value ?? NaN;
    return Number.isFinite(v) && Math.abs(v) > 1e-9 ? (v > 0 ? SIGN.pos : SIGN.neg) : SIGN.any;
  }
  const t = a.term;
  if (t.k === 'fn' && t.f === 'exp') return SIGN.pos;
  // n! ≥ 1 (moteur v27)
  if (t.k === 'fn' && t.f === 'fact') return SIGN.pos;
  if (t.k === 'fn' && (t.f === 'sqrt' || t.f === 'abs')) return SIGN.nonneg;
  return SIGN.any;
}

/** Rapport rationnel c tel que p = c × h, sinon null. */
function ratioTo(p: Poly, h: Poly): Q.Rat | null {
  const tp = polyTerms(p)[0];
  const th = polyTerms(h)[0];
  if (!tp || !th) return null;
  const c = Q.div(tp.c, th.c);
  return c && polyEqual(p, polyScale(h, c)) ? c : null;
}

/** Ensemble des signes possibles d'un polynôme. */
export function signOf(p: Poly, facts: Facts): SignSet {
  if (polyIsZero(p)) return SIGN.zero;
  const r = polyRat(p);
  if (r !== null) return Q.sign(r) > 0 ? SIGN.pos : SIGN.neg;
  let out: SignSet = SIGN.any;
  if (polyIsConstant(p)) {
    const v = polyFloat(p);
    if (Number.isFinite(v) && Math.abs(v) > 1e-9) return v > 0 ? SIGN.pos : SIGN.neg;
  }
  for (const h of facts.polys) {
    const c = ratioTo(p, h.p);
    if (c) out = meet(out, Q.sign(c) > 0 ? h.s : negSet(h.s));
  }
  // terme à terme
  const terms = polyTerms(p).map(({ m, c }) =>
    m.reduce<SignSet>(
      (acc, f) => mulSet(acc, powSet(atomSign(f.a, facts), f.e)),
      Q.sign(c) > 0 ? SIGN.pos : SIGN.neg,
    ),
  );
  let sum: SignSet = SIGN.any;
  if (terms.length === 1) sum = terms[0]!;
  else if (terms.every((t) => !t.neg))
    sum = { pos: terms.some((t) => t.pos), zero: terms.every((t) => t.zero), neg: false };
  else if (terms.every((t) => !t.pos))
    sum = { neg: terms.some((t) => t.neg), zero: terms.every((t) => t.zero), pos: false };
  return meet(out, sum);
}

/** Signe d'une fraction (le dénominateur est supposé non nul). */
export function signOfRatFn(r: RatFn, facts: Facts): SignSet {
  const d = signOf(r.d, facts);
  return mulSet(signOf(r.n, facts), { ...d, zero: false });
}

/* ------------------------------ décision ------------------------------ */

export type Decision = 'true' | 'false' | 'unknown';

const fromSet = (s: SignSet, kind: ConditionKind): Decision => {
  // aucune valeur possible (division par zéro sous les hypothèses) : condition fausse
  if (!s.pos && !s.zero && !s.neg) return 'false';
  const only = (want: keyof SignSet): boolean =>
    s[want] && (Object.keys(s) as (keyof SignSet)[]).every((k) => k === want || !s[k]);
  switch (kind) {
    case 'nonzero':
      return !s.zero ? 'true' : only('zero') ? 'false' : 'unknown';
    case 'positive':
      return !s.zero && !s.neg ? 'true' : !s.pos ? 'false' : 'unknown';
    case 'negative':
      return !s.zero && !s.pos ? 'true' : !s.neg ? 'false' : 'unknown';
    case 'nonnegative':
      return !s.neg ? 'true' : !s.pos && !s.zero ? 'false' : 'unknown';
    case 'zero':
      return only('zero') ? 'true' : !s.zero ? 'false' : 'unknown';
    default:
      return 'unknown';
  }
};

/** Une condition est-elle vraie, fausse, ou indécidable sous ces faits ? */
export function decide(c: Condition, facts: Facts): Decision {
  if (c.kind === 'real') return 'true';
  if (c.kind === 'defined_on') return 'unknown';
  const terms = c.of.map(exprOf);
  if (terms.some((t) => t === null)) return 'unknown';
  const rfs = terms.map((t) => normalize(t!, { sorts: facts.sorts }));
  if (rfs.some((r) => !r.ok)) return 'unknown';
  const rs = rfs.map((r) => (r as { ok: true; rf: RatFn }).rf);
  if (c.kind === 'distinct') {
    let all: Decision = 'true';
    for (let i = 0; i < rs.length; i++)
      for (let j = i + 1; j < rs.length; j++) {
        if (rfEqual(rs[i]!, rs[j]!)) return 'false';
        const n = normalize(
          { k: 'add', c: [terms[i]!, { k: 'mul', c: [{ k: 'num', v: Q.rat(-1) }, terms[j]!] }] },
          { sorts: facts.sorts },
        );
        const d = n.ok ? fromSet(signOfRatFn(n.rf, facts), 'nonzero') : 'unknown';
        if (d !== 'true') all = 'unknown';
      }
    return all;
  }
  const r = rs[0]!;
  if (c.kind === 'integer' || c.kind === 'natural') {
    const v = polyRat(r.d) !== null ? polyRat(r.n) : null;
    const t = terms[0]!;
    const isInt =
      v !== null
        ? Q.isInteger(Q.div(v, polyRat(r.d)!)!)
        : t.k === 'sym' && facts.integer.has(t.n)
          ? true
          : null;
    if (isInt === null) return 'unknown';
    if (!isInt) return 'false';
    return c.kind === 'integer' ? 'true' : fromSet(signOfRatFn(r, facts), 'nonnegative');
  }
  return fromSet(signOfRatFn(r, facts), c.kind);
}

/** Condition sur un polynôme : `{ kind: 'nonzero', of: ['x − 1'] }`. */
export function conditionOn(kind: ConditionKind, t: Term): Condition {
  return { kind, of: [termText(t)] };
}

/** Polynôme non nul sous les faits ? (raccourci) */
export function isNonzero(p: Poly, facts: Facts): Decision {
  return fromSet(signOf(p, facts), 'nonzero');
}
export function signDecision(p: Poly, facts: Facts): 'pos' | 'neg' | 'zero' | 'unknown' {
  const s = signOf(p, facts);
  if (s.pos && !s.zero && !s.neg) return 'pos';
  if (s.neg && !s.zero && !s.pos) return 'neg';
  if (s.zero && !s.pos && !s.neg) return 'zero';
  return 'unknown';
}
